import React, { useEffect, useMemo, useRef, useState } from "react";
import ScreenOverlay from "../../components/dms_common/ScreenOverlay";
import { Alert, FormControl, Select, MenuItem, Snackbar, Button } from "@mui/material";
import {
  filterData,
  searchWithStatusFilter,
  updateDatesInLocalStorage,
  getMultipleFilterBasedData,
} from "../../components/dms_common/DmsCommonServices";
import { Loader } from "../../components/loader/Loader";
import {
  GenericModal,
  getGenericModalProps,
  initialGenericModalProps,
} from "../viewOrder/ViewOrderUtils";
import CustomGenericModal from "../../components/dms_common/CustomGenericModal";
import HeaderV2 from "../../components/dms_common/HeaderV2";
import { getConfigData } from "../../services/configurationService";
import {
  DownloadSingleReportConfig,
  managePrimaryOMSOrderRowsData,
  orderStatusConfig,
  orderSourceConfig,
} from "../../types/types";
import { capitalize, capitalizeFirstLetter } from "../../utils/utils";
import { ReactComponent as DownloadAllIcon } from "../../assets/image/svg/downloadAllWhite.svg";

import {
  getDownloadSingleReportConfig,
  getOrderSource,
} from "../secondaryOrder/ManageSecondaryOrderUtils";
import { sendSlackAlert, SlackChannel } from "../../services/slackService";
import {
  formatAmount,
  safeStringify,
} from "../../components/dms_common/DmsUtils";
import { getOrderList, getUTCDate } from "../../services/manageOrderService";
import { downloadAllReportsPrimarySecondary } from "../../services/manageReportService";
import useSessionStorageDates from "../../components/dms_common/UseSessionStorageDates";
import dayjs, { Dayjs } from "dayjs";
import GenericSummaryCard from "../../components/dms_common/GenericSummaryCard";
import { GenericSummaryCardItemObject } from "../../components/dms_common/GenericSummaryCard";
import CustomDataGrid from "../../components/dms_common/CustomDataGrid";
import { managePrimaryOrderColumnsMetaDataV2 } from "./ManagePrimaryOrderUtils";
import {
  isGapMoreThanNDays,
  isStartDateIsGreaterThanEndDate,
} from "../../utils/calenderUtils";
import TextSwitcher, {
  TextSwitcherItemObject,
} from "../../components/dms_common/TextSwitcher";

const ManagePrimaryOrderOMSV2 = () => {
  const { storedStartDate, storedEndDate, storeSessionStorageDates } =
    useSessionStorageDates("managePrimaryOMSOrders");
  const ConfigDictionary = JSON.parse(
    localStorage.getItem("ConfigDictionary") ?? "{}"
  );
  const feature = "Sale";
  const pageID = "ManageOrderPrimaryOMSV2";

  const [data, setData] = useState<managePrimaryOMSOrderRowsData[]>([]);
  const [rows, setRows] = useState<managePrimaryOMSOrderRowsData[]>([]);
  const currencySymbol = ConfigDictionary?.currency ?? "";
  const [multipleFiltersVisible, setMultipleFiltersVisible] =
    useState<boolean>(false);
  const [multipleFiltersList, setMultipleFiltersList] = useState<
    Record<string, Record<string, number>>
  >({});

  const [orderTrackingStagesList, setOrderTrackingStagesList] = useState<
    orderStatusConfig[]
  >([]);
  const [showSiteStatusFilter, setShowSiteStatusFilter] =
    useState<boolean>(false);
  const [showSiteId, setShowSiteId] = useState(false);
  const allOrdersReportIdRef = useRef<string | null>(null);
  const [filterKeys, setFilterKeys] = useState<string[]>([]);
  const [multipleFilterStatusLabelMap, setMultipleFilterStatusLabelMap] =
    useState<Record<string, string>>({ status: "Status" });
  const orderSourcesRef = useRef<Record<string, orderSourceConfig>>({});
  const [downloadSingleReportConfig, setDownloadSingleReportConfig] =
    useState<DownloadSingleReportConfig | null>();
  const [startDate, setStartDate] = useState<Dayjs>(storedStartDate);
  const [endDate, setEndDate] = useState<Dayjs>(storedEndDate);
  const [lastRefreshed, setLastRefreshed] = useState<Dayjs>(dayjs());
  const [loader, setLoader] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [genericModalProps, setGenericModalProps] = useState<GenericModal>(
    initialGenericModalProps
  );
  const [isOptionsRightHovered, setIsOptionsRightHovered] = useState(false);
  const [textSwitcherItems, setTextSwitcherItems] = useState<
    TextSwitcherItemObject[]
  >([]);

  const [summaryItems, setSummaryItems] = useState<
    GenericSummaryCardItemObject[]
  >([]);
  const isDownloadingRef = useRef(false);
  const [resetSearch, setResetSearch] = useState(false);
  const [searchedValue, setSearchedValue] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedFilterType, setSelectedFilterType] = useState("status");
  const [isProcessing, setIsProcessing] = useState(false);
  const [checkedRows, setCheckedRows] = useState<string[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >(() => {
    const stored = localStorage.getItem("multipleFiltersListManagePrimaryOMS");
    return stored ? JSON.parse(stored) : {};
  });
  const validDateRange = ConfigDictionary.dateRangeConfig ?? 31;
  const [sortByColumn, setSortByColumn] = useState<any>();

  const handleSearchValueChange = (newValue: string) => {
    setSearchedValue(newValue);
  };

  function canWeMakeRequest() {
    if (
      startDate &&
      endDate &&
      isGapMoreThanNDays(startDate, endDate, validDateRange)
    ) {
      setGenericModalProps(
        getGenericModalProps(
          "positive",
          `You can't select date range more than ${validDateRange} days!`
        )
      );
      return false;
    }
    if (
      startDate &&
      endDate &&
      isStartDateIsGreaterThanEndDate(startDate, endDate)
    ) {
      setGenericModalProps(
        getGenericModalProps(
          "positive",
          "To Date should be greater than from date."
        )
      );
      return false;
    }
    updateDatesInLocalStorage(startDate, endDate, feature, pageID);
    return true;
  }
  useEffect(() => {
    if (sortByColumn && sortByColumn.key && sortByColumn.direction !== "none") {
      const { key: sortKey, direction, type } = sortByColumn;

      const sortedRows = [...rows].sort((a: any, b: any) => {
        const valA = a[sortKey];
        const valB = b[sortKey];

        if (valA == null && valB == null) return 0;
        if (valA == null) return direction === "asc" ? -1 : 1;
        if (valB == null) return direction === "asc" ? 1 : -1;

     
        if (type === "number") {
          return direction === "asc"
            ? Number(valA) - Number(valB)
            : Number(valB) - Number(valA);
        }

        if (type === "date") {
          const dateA = new Date(valA).getTime();
          const dateB = new Date(valB).getTime();

          if (isNaN(dateA) || isNaN(dateB)) {
            return direction === "asc"
              ? String(valA).localeCompare(String(valB))
              : String(valB).localeCompare(String(valA));
          }

          return direction === "asc" ? dateA - dateB : dateB - dateA;
        }

        return direction === "asc"
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      });

      setRows(sortedRows);
    }
  }, [sortByColumn]);

  const fetchRequiredConfigs = async () => {
    try {
      const stages = ConfigDictionary?.primaryOrderTrackingStages;
      stages.unshift({
        id: "ALL",
        color: "#F0F3FF",
        dmsLabel: "All Orders",
        labelColor: "",
      });
      const orderReportIdPConfig = await getConfigData(
        "dms_configuration",
        "orderReportIdPrimary"
      );
      const orderReportIdConfig = await getConfigData(
        "dms_configuration",
        "orderReportId"
      );
      const orderSourcesConfig = await getConfigData(
        "dms_configuration",
        "orderSources"
      );
      const res = await getConfigData("dms_configuration", "showSiteId");
      const res1 = await getConfigData(
        "dms_configuration",
        "enableSiteStatusFilter"
      );
      setShowSiteStatusFilter(res1[0]?.configValue ?? false);
      setShowSiteId(res[0]?.configValue ?? false);
      const res9 = await getConfigData(
        "dms_configuration",
        "allPrimaryOrdersReportId"
      );
      const res10 = await getConfigData(
        "dms_configuration",
        "multipleFilterStatusLabelMap"
      );
      const filterKeysConfig = await getConfigData(
        "dms_configuration",
        "multiFilterKeysManagePrimaryOMS"
      );
      let defaultKeys = ["status"];
      const siteKeyValue = res[0]?.configValue ?? false;
      if (siteKeyValue) {
        defaultKeys.push("siteId");
      }
      setFilterKeys(filterKeysConfig[0]?.configValue ?? defaultKeys);
      const orderReportId =
        orderReportIdPConfig[0]?.configValue ??
        orderReportIdConfig[0]?.configValue ??
        "";
      const orderSourcesMapping: Record<string, orderSourceConfig> = {};
      orderSourcesConfig[0]?.configValue?.forEach(
        (sourceObj: orderSourceConfig) => {
          orderSourcesMapping[sourceObj.id] = sourceObj;
        }
      );
      setMultipleFilterStatusLabelMap(
        res10[0]?.configValue ?? { status: "status" }
      );
      orderSourcesRef.current = orderSourcesMapping;
      allOrdersReportIdRef.current = res9[0]
        ? res9[0]?.configValue
        : orderReportId;
      setOrderTrackingStagesList(stages);
      setDownloadSingleReportConfig(
        getDownloadSingleReportConfig(orderReportId)
      );
    } catch (e) {
      await sendSlackAlert(
        SlackChannel.DMS_PRIMARY_UI_NOTIFICATION,
        "Unexpected Error Fetching Configs - ManagePrimaryOrder",
        `Unexpected error while fetching configurations in ManagePrimaryOrder. Error: ${
          (e as Error).message
        }`,
        safeStringify({ e, file: "ManagePrimaryOrder.tsx" })
      );
      console.error("Error while fetching configs", e);
    }
  };

  const filterFullyConsumedOrders = (orders: any[]) => {
    // Step 1: Build a map: originalOrderNumber -> Set of consumed IDs (retainId)
    const consumedByOrder = new Map<string, Set<string>>();
  
    orders.forEach(order => {
      const refOrderNum = order.referenceOrderNumber;
  
      if (refOrderNum) {
        if (!consumedByOrder.has(refOrderNum)) {
          consumedByOrder.set(refOrderNum, new Set());
        }
  
        // For each detail in the reference order, collect retainId
        order.orderDetails?.forEach((detail: any) => {
          const retainId = detail?.extendedAttributes?.retainId;
          if (retainId && detail.type !== "freeItem") {
            consumedByOrder.get(refOrderNum)!.add(retainId);
          }
        });
      }
    });
  
    // Step 2: Filter orders that are NOT fully consumed
    const filteredOrders = orders.filter(order => {
      const orderNum = order.orderNumber;
  
      // If no reference orders consumed from this, keep it
      if (!consumedByOrder.has(orderNum)) return true;
  
      // Get all original IDs from this order (excluding freeItem)
      const originalIds = order.orderDetails
        ?.filter((detail: any) => detail.type !== "freeItem")
        ?.map((detail: any) => detail.id) || [];
  
      // If no valid items, keep it
      if (originalIds.length === 0) return true;
  
      const consumedIds = consumedByOrder.get(orderNum)!;
  
      // Check if ALL original IDs are in consumedIds
      const allConsumed = originalIds.every((id:any) => consumedIds.has(id));
  
      // Keep only if NOT fully consumed
      return !allConsumed;
    });
  
    return filteredOrders;
  };
  

  const getInitalTableData = async () => {
    if (canWeMakeRequest()) {
      storeSessionStorageDates(startDate, endDate);
      const start = getUTCDate(startDate, "start");
      const end = getUTCDate(endDate, "end");
      await getAllOrderData(start, end);
    }
  };

  const getAllOrderData = async (startDate: string, endDate: string) => {
    setLastRefreshed(dayjs());
    setResetSearch(true);
    setTimeout(() => setResetSearch(false), 0);

    try {
      setLoader(true);

      const response = await getOrderList(startDate, endDate, "primary");
      if (response?.data?.features?.length > 0) {
        let responseArray: managePrimaryOMSOrderRowsData[] = [];
        response?.data?.features?.forEach((item: any, index: any) => {
          const {
            id,
            outletName,
            outletCode,
            creationTime,
            totalCaseQuantity,
            totalPieceQuantity,
            totalOtherUnitQuantity,
            netAmount,
            status,
            orderNumber,
            salesRepName,
            salesRepId,
            invoiceNumber,
            eInvoice,
            areaCode,
            orderDetails,
            normalizedQuantity = 0,
            referenceOrderNumber,
          } = item || {};

          let totalCases = 0

          orderDetails?.forEach((obj: any, index: any) => {
            const {
              pieceQuantity,
              caseQuantity,
              otherUnitQuantity,
              productInfo,
            } = obj;

            const caseToPieceQuantity = productInfo?.caseToPieceQuantity ?? 0;
            const otherUnitToPieceQuantity =
              productInfo?.otherUnitToPieceQuantity ?? 0;
    
          const totalConvertedCasesPrimaryOMS = Math.trunc(
            caseQuantity +
              (otherUnitToPieceQuantity === 0
              ? 0
              : otherUnitQuantity *
                (otherUnitToPieceQuantity / caseToPieceQuantity)) +
            (caseToPieceQuantity === 0
              ? 0
              : pieceQuantity / caseToPieceQuantity))
              
              totalCases += totalConvertedCasesPrimaryOMS;
          });

          const extraParams = status === "INVOICED" ? `?type=invoice` : ``;
          const orderSource = getOrderSource(item);

          const isCancelledInvoice: boolean =
            status?.toLowerCase() == "rejected" && invoiceNumber ? true : false;

          const siteId = item?.extendedAttributes?.siteId ?? "";

          responseArray.push({
            id,
            areaCode,
            outletName,
            outletCode,
            creationTime,
            totalCaseQuantity,
            totalPieceQuantity,
            totalOtherUnitQuantity,
            netAmount: netAmount,
            status: status
              .toLowerCase()
              .replace(/(?:^|\s|&)\S/g, (match: any) => match?.toUpperCase()),
            orderNumber,
            source: orderSource,
            extraParams,
            salesRepName,
            salesRepId,
            checkOrderStatus: true,
            eInvoiced: eInvoice === "true",
            cancelledInvoice: isCancelledInvoice,
            orderDetails,
            siteId,
            normalizedQuantity,
            totalConvertedCases: totalCases + " " + (ConfigDictionary?.caseUnitLabelPrimary ?? ""),
            referenceOrderNumber,
          });
        });

        const filteredOrders = filterFullyConsumedOrders(responseArray);
  

        setRows(filteredOrders);
        setData(filteredOrders);
        setStatusFilter("ALL");
        setSearchedValue("");
      } else {
        setRows([]);
        setData([]);
      }
    } catch (error) {
      await sendSlackAlert(
        SlackChannel.DMS_PRIMARY_UI_NOTIFICATION,
        "Error Fetching Orders - ManagePrimaryOrder",
        `Failed to fetch order data for startDate ${startDate} and endDate ${endDate} in ManagePrimaryOrder. Error: ${
          (error as Error).message
        }`,
        safeStringify({
          error,
          startDate,
          endDate,
          file: "ManagePrimaryOrder.tsx",
        })
      );
      setRows([]);
      setGenericModalProps(
        getGenericModalProps("error", "Something went wrong")
      );
    } finally {
      setLoader(false);
    }
  };

  const filteredData = useMemo(() => {
    const labelToIdMap = new Map<string, string>();

    orderTrackingStagesList.forEach((stage) => {
      if (stage?.dmsLabel && stage?.id) {
        labelToIdMap.set(stage.dmsLabel.toUpperCase(), stage.id);
      }
    });

    let statuses: string[] = [];
    if (filterKeys.includes("status")) {
      (selectedFilters["status"] || []).forEach((status) => {
        statuses = [
          ...statuses,
          ...(labelToIdMap
            .get(status.toUpperCase())
            ?.split(",")
            .map((label) => label.trim()) ?? []),
        ];
      });
    }

    const updatedSelectedFilters = { ...selectedFilters };
    if (filterKeys.includes("status")) {
      updatedSelectedFilters["status"] = statuses;
    }

    const multipleFilteredData = showSiteStatusFilter
      ? getMultipleFilterBasedData(data, updatedSelectedFilters, filterKeys)
      : data;

    const searchFilteredData = filterData(
      multipleFilteredData,
      searchedValue?.toLowerCase(),
      searchWithStatusFilter,
      statusFilter
    );

    const filteredResults = multipleFilteredData.filter((item: any) =>
      new Set(searchFilteredData).has(item)
    );

    return filteredResults;
  }, [searchedValue, statusFilter, data, selectedFilters, filterKeys]);

  useEffect(() => {
    init();
  }, [startDate, endDate]);

  useEffect(() => {
    calculateOrderCount();
    getTextSwitcherItems();
  }, [startDate,endDate,rows]);

  const getTextSwitcherItems = () => {
    
    setTextSwitcherItems(
      [
            {
              label: "Quick Action: ",
              text: `Select a date range and `,
              svgIcon: "stopWatchSVG",
              itemStyles: {
                container: {
                  backgroundColor: "#FFF1FE",
                  padding: "5px 10px",
                  borderRadius: "20px",
                  width:"fit-content"
                },
                label: {
                  fontSize: "12px", 
                },
                text: {
                  color: "#7B087B",
                  fontSize: "12px",
                },
                svg: {
                  marginRight:"3px",
                  paddingTop:"6px",
                  width: "0.8em",
                },
              },
              actionButton: (
                <button
                  onClick={()=> handleDownloadAll(true)}
                  style={{
                    padding: "4px 8px",
                    textDecoration: "underline",
                    background: "none",
                    color: "#1B7713",
                    border: "none",
                    fontSize: "12px", 
                    fontWeight: 600,
                  }}
                >
                  {"Bulk download data"}
                </button>
              ),
            }, 
    ]);
  };

  useEffect(() => {
    setRows(filteredData);
  }, [filteredData]);

  const handleStatusFilterChange = (newFilter: string) => {
    setStatusFilter(newFilter);
  };

  const handleSelectedFilterChange = (key: string, value: string) => {
    setSelectedFilters((prev) => {
      const current = prev[key] || [];
      return {
        ...prev,
        [key]: current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value],
      };
    });
  };
  const calculateOrderCount = () => {
    let pendingCount = 0;
    let invoicedCount = 0;
    let pendingAmount = 0;
    let invoicedAmount = 0;

    rows.forEach((item) => {
      if (item?.status?.toUpperCase() === "PENDING") {
        pendingCount += 1;
        pendingAmount += parseFloat(item.netAmount);
      }
      if (item?.status?.toUpperCase() === "INVOICED") {
        invoicedCount += 1;
        invoicedAmount += parseFloat(item.netAmount);
      }
    });
    setSummaryItems([
      {
        itemLabel: "Total Pending Orders",
        itemValue1: `${pendingCount}`,
        itemValue2: formatAmount(pendingAmount, currencySymbol),
        svgIcon: "pendingOrdersSVG",
        itemStyles: {
          itemValue1: {
            fontWeight: "bold",
          },
          itemValue2: {
            color: "#CC8034",
            backgroundColor: "#FFFBE7",
          },
        },
      },
      {
        itemLabel: "Total Invoiced Orders",
        itemValue1: `${invoicedCount}`,
        itemValue2: formatAmount(invoicedAmount, currencySymbol),
        svgIcon: "invoicedOrdersSVG",
        itemStyles: {
          itemValue1: {
            fontWeight: "bold",
          },
          itemValue2: {
            color: "#5A865A",
            backgroundColor: "#EAFFEA",
          },
        },
      },
    ]);
  };

  useEffect(() => {
    const counts: Record<string, Record<string, number>> = {};
    filterKeys.forEach((key) => {
      counts[key] = {};
    });

    if (filterKeys.includes("status")) {
      const labelToIdMap = new Map<string, string>();
      let allDmsLabel: string | undefined;
      orderTrackingStagesList.forEach((stage) => {
        if (stage?.dmsLabel && stage?.id) {
          labelToIdMap.set(stage.dmsLabel.toUpperCase(), stage.id);
          if (stage.id === "ALL") {
            allDmsLabel = stage.dmsLabel.toUpperCase();
          }
        }
      });

      orderTrackingStagesList.forEach((stage: any) => {
        if (stage?.dmsLabel && stage.dmsLabel.toUpperCase() !== allDmsLabel) {
          counts["status"][stage.dmsLabel.toUpperCase()] = 0;
        }
      });

      data.forEach((row: any) => {
        const rowStatus = row?.status?.toUpperCase();
        Object.keys(counts["status"]).forEach((key) => {
          const labels =
            labelToIdMap
              .get(key)
              ?.split(",")
              .map((label) => label.trim()) ?? [];
          if (labels.includes(rowStatus)) {
            counts["status"][key] += 1;
          }
        });
      });
    }

    filterKeys
      .filter((key) => key !== "status")
      .forEach((key) => {
        data.forEach((row: any) => {
          const value = row[key];
          if (typeof value === "string" && value) {
            counts[key][value.toLowerCase()] =
              (counts[key][value.toLowerCase()] || 0) + 1;
          }
        });
      });

    setMultipleFiltersList(counts);
  }, [orderTrackingStagesList, data, filterKeys]);


  const handleFilterTypeChange = (type: string) => {
    setSelectedFilterType(type);
  };

  const init = async () => {
    setLoader(true);
    setCheckedRows([])
    await fetchRequiredConfigs();
    await getInitalTableData();
  };

  useEffect(() => {
    init();
  }, []);

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleSetCheckedIdArray = (ids: string[]) => {
    setCheckedRows(ids);
  };


  const DownloadFooterElement = () => (
    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", height: "100%", padding: "8px 0px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <div style={{ color: "#666", fontSize: "14px", marginRight: "8px" }}>
        </div>
        <Button
          variant="contained"
          disabled={isProcessing || checkedRows.length === 0}
          sx={{
            backgroundColor: checkedRows.length > 0 ? "#00A392" : "#B0B0B0",
            color: "white",
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
            padding: "8px 16px",
            height: "36px",
            minWidth: "120px",
            "&:hover": {
              backgroundColor: checkedRows.length > 0 ? "#008B7A" : "#B0B0B0",
            },
            "&:disabled": {
              backgroundColor: "#B0B0B0",
              color: "white",
            },
          }}
          onClick={()=> handleDownloadAll(false)}
        >
          Download
        </Button>
      </div>
    </div>
  );

  const handleDownloadAll = async (dateRange: boolean) => {
     if (isDownloadingRef.current) return;
     isDownloadingRef.current = true;
    
     try {
      setIsProcessing(true);
      
      if(dateRange) {
        if(rows.length !== 0){
          await downloadAllReportsPrimarySecondary(
            allOrdersReportIdRef.current,
            setSnackbarOpen,
            startDate,
            endDate,
            "type",
            "primary",
            setIsProcessing
          );
      }
     } else {
        const selectedOrders = rows.filter(row => checkedRows.includes(row.id));
        const orderIds = selectedOrders.map(order => order.id);

        await downloadAllReportsPrimarySecondary(
          allOrdersReportIdRef.current,
          setSnackbarOpen,
          startDate,
          endDate,
          "type",
          "primary",
          setIsProcessing,
          downloadSingleReportConfig,
          orderIds,
          rows
        );
        setCheckedRows([]);
     }
    } catch (error) {
      console.error("Error downloading reports:", error);
    }finally {
      isDownloadingRef.current = false;
      setIsProcessing(false);
    }
  };

  return (
    <div className="manage_order_parent">
      <ScreenOverlay apply={loader} />

      <CustomGenericModal
        type={genericModalProps?.type}
        message={genericModalProps?.message}
        leftButtonText={genericModalProps?.leftButtonText}
        modalOpen={genericModalProps?.open}
        onClose={() => setGenericModalProps(initialGenericModalProps)}
      />

      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Your report will be downloaded shortly.
        </Alert>
      </Snackbar>

      <HeaderV2
        title={"Manage Orders"}
        searchFunction={loader ? () => {} : handleSearchValueChange}
        onRefresh={init}
        resetSearch={resetSearch}
        lastRefreshed={lastRefreshed}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        startDate={startDate}
        endDate={endDate}
      />
      <div style={{ fontSize: "0.75rem" }}>
        <GenericSummaryCard
          cardTitle="Sales Order Summary"
          itemObjects={summaryItems}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "9px",
        }}
      >
        <TextSwitcher
          textSwitcherItems={textSwitcherItems ?? []}
          interval={3000}
        />
        
        <div
          style={{
            display: "flex",
            gap: "8px",
            height: "2.6em",
            width: "100%",
            justifyContent: "end",
            maxWidth: "500px",
            alignItems: "center",
          }}
        >
          <div className="order-status-filters" style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <div
              className="order-status-button"
              onClick={() => {
                setSelectedFilters({});
              }}
              style={{
                padding: "0px 12px",
                fontSize: "12px",
                backgroundColor: Object.values(selectedFilters ?? {}).some(
                  (arr) => arr?.length > 0
                )
                  ? "#FAFAFB"
                  : "#00A392",
                borderRadius: "16px",
                cursor: "pointer",
                height: "30px",
                fontWeight: "500",
                border: "none",
                transition: "all 0.2s",
                width: "fit-content",
                minWidth: "max-content",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  color: Object.values(selectedFilters ?? {}).some(
                    (arr) => arr?.length > 0
                  )
                    ? "#7c7c7c"
                    : "white",
                  lineHeight: 1,
                }}
              >
                All
              </span>
            </div>
            {Object.keys(multipleFiltersList["status"] ?? {})
              .slice(0, 4)
              .map((value) => (
                <div
                  className="order-status-button"
                  onClick={() => handleSelectedFilterChange("status", value)}
                  style={{
                    padding: "0px 12px",
                    fontSize: "12px",
                    backgroundColor: (selectedFilters["status"] || []).includes(
                      value
                    )
                      ? "#00A392"
                      : "#FAFAFB",
                    borderRadius: "16px",
                    cursor: "pointer",
                    height: "30px",
                    fontWeight: "500",
                    border: "none",
                    transition: "all 0.2s",
                    width: "fit-content",
                    minWidth: "max-content",
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      color: (selectedFilters["status"] || []).includes(value)
                        ? "white"
                        : "#7c7c7c",
                      lineHeight: 1,
                    }}
                  >
                    {capitalizeFirstLetter(value)}
                  </span>
                </div>
              ))}
          </div>

          {showSiteStatusFilter ? (
            <FormControl className="manage_secondary_orders_filteV2" sx={{
              "& .MuiInputBase-root": {
                fontSize: "inherit !important",
                height: "30px",
                marginTop: "0px",
                minWidth: "80px",
              },
              display: "flex",
              alignItems: "center",
              }}>
             
              <Select
                variant="outlined"
                labelId="dropdown_label"
                open={multipleFiltersVisible}
                value="Filter Orders"
                onOpen={() => {
                  setMultipleFiltersVisible(true);
                }}
                onClose={() => setMultipleFiltersVisible(false)}
                renderValue={() => (
                  <span style={{ color: Object.values(selectedFilters)?.some(arr => arr?.length > 0)
                    ? "white"
                    : "#7C7C7C",
                  fontSize: "12px",
                  fontWeight: 500,
                  transition: "background-color 0.3s ease-in-out",  }}
                  className="manage_secondary_orders_filter-labelV2"
                  >
                    More Filters
                  </span>
                )}
                sx={{
                  background: Object.values(selectedFilters)?.some(
                    (arr) => arr?.length > 0
                  )
                    ? "#00A392"
                    : "#FAFAFB",

                  boxSizing: "border-box",
                  height: "30px",
                  width: "fit-content",
                  borderRadius: "20px",
                  "& .MuiInputBase-root": {
                    fontSize: "inherit !important",
                  },
                  "& .MuiSelect-select": {
                    fontSize: "12px",
                    height: "30px",
                    padding: "0px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    color: "inherit",
                    outline: "none",
                    border: "none",
                    justifyContent: "center",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none", // removes border
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused": {
                    outline: "none",
                    boxShadow: "none",
                  },
                  "& .MuiSelect-icon": {
                    fontSize: "0.875em",
                    color: Object.values(selectedFilters)?.some(
                      (arr) => arr?.length > 0
                    )
                      ? "white"
                      : "#7C7C7C",
                    right: "6px",
                  },
                }}
             
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "right",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "right",
                  },
                  PaperProps: {
                    sx: {
                      boxShadow: "none",
                      marginTop: "4px",
                      maxHeight: "600px",
                      border: "1px solid #DEDEDE",
                    },
                  },
                  MenuListProps: {
                    sx: {
                      paddingTop: "6px",
                      paddingBottom: "0",
                    },
                  },
                }}
              >
                <MenuItem
                  key={`filter-${selectedFilterType}-${JSON.stringify(
                    selectedFilters
                  )}`}
                  disableRipple
                  style={{
                    padding: 0,
                    backgroundColor: "transparent",
                    outline: "none",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <div
                    className="MSO_filter-dropdown-container"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="MSO_filter-header">
                      <span className="MSO_filter-name">All Filters</span>
                      <span
                        className={`MSO_filter-clear-all ${
                          Object.values(selectedFilters).some(
                            (arr) => arr.length > 0
                          ) &&
                          `MSO_filter-clear-all_active`
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFilters({});
                        }}
                      >
                        Clear All
                      </span>
                    </div>

                    <div className="MSO_filter-options">
                      <div className="MSO_filter-options-left" style={{width:"100px"}}>
                        {filterKeys.map((key) => (
                          <div
                            key={key}
                            className={`${
                              selectedFilterType === key && `MSO_filterSelected`
                            }`}
                            onClick={(e) => {
                              handleFilterTypeChange(key);
                              e.stopPropagation();
                            }}
                          >
                            {multipleFilterStatusLabelMap?.[key] ?? key} (
                            {(selectedFilters[key] || []).length})
                          </div>
                        ))}
                      </div>
                      <div className="MSO_filter-options-right" 
                      style={{
                        maxHeight: "500px", 
                        overflowY: isOptionsRightHovered ? "auto" : "hidden",
                        padding: "10px 0",  
                      }}
                      onMouseEnter={() => setIsOptionsRightHovered(true)}
                      onMouseLeave={() => setIsOptionsRightHovered(false)}
                      >
                        {filterKeys.map(
                          (key) =>
                            selectedFilterType === key && (
                              <div className="MSO_filter-option" key={key}>
                                {Object.keys(multipleFiltersList[key] ?? {})
                                  .map((value) => (
                                    <label
                                      key={value}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        color: "#444444",
                                        fontSize: "13px",
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={(
                                          selectedFilters[key] || []
                                        ).includes(value)}
                                        onChange={() =>
                                          handleSelectedFilterChange(key, value)
                                        }
                                        style={{ borderColor: "#CBCBCB" }}
                                      />
                                      {`${capitalizeFirstLetter(value)} (${
                                        multipleFiltersList[key]?.[value] || 0
                                      })`}
                                    </label>
                                  ))}
                              </div>
                            )
                        )}
                      </div>
                    </div>
                  </div>
                </MenuItem>
              </Select>
            </FormControl>
          ) : (
            <FormControl className="manage_order_filter">
              <span
                style={{
                  fontSize: "14px",
                  color: "#AFAFAF",
                }}
              ></span>
              <Select
                labelId="dropdown_label"
                value={statusFilter}
                size="small"
                sx={{ minWidth: "190px", marginLeft: "10px" }}
                onChange={(e) => {
                  handleStatusFilterChange(e.target.value);
                }}
                renderValue={(selected) => {
                  const item = orderTrackingStagesList.find(
                    (d) => d.id === selected
                  );
                  return item ? (
                    <div className="dropdown_selected_item">
                      {capitalize(item?.dmsLabel ?? "")}
                    </div>
                  ) : null;
                }}
              >
                {orderTrackingStagesList.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    <div className="dropdown_menu_item">
                      <span>{capitalize(item?.dmsLabel ?? "")}</span>
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>
      </div>


      {loader ? (
        <Loader sx={{ position: "absolute", top: "55%" }} />
      ) : (
        <CustomDataGrid
          rows={Array.isArray(rows) ? rows : []}
          columnsMetaData={managePrimaryOrderColumnsMetaDataV2(showSiteId)}
          orderStatusConfig={orderTrackingStagesList}
          redirectUrl="/primaryOms/manageOrders/viewOrder/"
          tableWidth={0.745}
          downloadSingleReportConfig={downloadSingleReportConfig}
          orderSourceConfig={orderSourcesRef.current}
          enableRowClickRedirect={true}
          setSortByColumn={setSortByColumn}
          isCheckboxSelection={true}
          isFooterVisible={true}
          footerElement={DownloadFooterElement}
          checkedIdArray={checkedRows}
          setCheckedIdArray={handleSetCheckedIdArray}
        />
      )}
    </div>
  );
};
export default ManagePrimaryOrderOMSV2;
